import type {
  ProposalCollaborationEnvelope,
  ProposalContentOperation,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { describeProposalOperations } from "work/proposals/negotiation/proposal-change-descriptors";
import { proposalFieldForTimelinePath } from "work/proposals/negotiation/proposal-field-timeline-path";
import { proposalPublishableChanges } from "work/proposals/negotiation/proposal-publishable-changes";
import type { ProposalFieldCategoryType } from "work/proposals/proposal-fields/proposal-field";

/**
 * How many queued-but-unsaved changes each tab is holding — the "N unsaved changes" chip.
 *
 * Counted through `describeProposalOperations`, not off the raw queue, so this number is by construction
 * the number of rows the Save confirmation is about to list: an operation that dialog declines to
 * describe (projection metadata, a resolution decision, a no-op field set) is not a change a user can
 * be shown, and advertising it on a tab they then find nothing on is worse than showing nothing.
 *
 * Operations, not fields: the chip says "changes", and the Save dialog itemises one row per operation,
 * so a user can check the badge against the list. (The unviewed-change badge beside it counts FIELDS —
 * a different question, deliberately, and the two are asserted together in `badges-and-history.spec.ts`.)
 */
export function proposalPendingChangeCountsByCategory(
  operations: readonly ProposalContentOperation[] | undefined,
): Partial<Record<ProposalFieldCategoryType, number>> {
  const counts: Partial<Record<ProposalFieldCategoryType, number>> = {};
  if (!operations?.length) return counts;

  // One per described row, so a document replacement — an add and a removal in the queue — counts once,
  // as the Save dialog lists it once (#5562).
  for (const descriptor of describeProposalOperations(operations)) {
    // Resolved by field, not by string-matching the path: a fee-category edit lands on
    // "/feeSchedule/categories" while Fee Schedule's own path is "/feeSchedule". Matching on the path
    // is what once left every collection-rooted tab stuck at zero (see `proposal-field-unread.ts`).
    const category = proposalFieldForTimelinePath(descriptor.path)?.category;
    if (!category) continue;
    counts[category] = (counts[category] ?? 0) + 1;
  }

  return counts;
}

/**
 * How many saved-but-unsubmitted changes each tab is holding — the "N changes not yet submitted" chip.
 *
 * A save takes a change out of the unsaved count above, but the counterparty still cannot see it until
 * the next submission (#5812). Counted from `proposalPublishableChanges`, the list the Submit dialog
 * offers, so the chip and the dialog agree on what a submission would send.
 */
export function proposalUnsubmittedChangeCountsByCategory(
  envelope: ProposalCollaborationEnvelope | undefined,
): Partial<Record<ProposalFieldCategoryType, number>> {
  const counts: Partial<Record<ProposalFieldCategoryType, number>> = {};
  if (!envelope) return counts;

  for (const change of proposalPublishableChanges(envelope)) {
    const category = proposalFieldForTimelinePath(change.path)?.category;
    if (!category) continue;
    counts[category] = (counts[category] ?? 0) + 1;
  }

  return counts;
}

/**
 * How many of the counterparty's incoming changes each tab holds that the viewer's side has already
 * decided — accepted, kept, or countered (#7086).
 *
 * Counted from `resolutionStates`, which the server scopes to the counterparty's latest submission and to
 * the decisions the viewer's party recorded after its own last submission. So this is "resolved this
 * round, by my side": once the viewer submits, the targets leave the envelope and the count returns to
 * zero, as the unsubmitted count does. One per resolution target, the unit the redline asks a decision on.
 */
export function proposalResolvedChangeCountsByCategory(
  envelope: Pick<ProposalCollaborationEnvelope, "resolutionStates"> | undefined,
): Partial<Record<ProposalFieldCategoryType, number>> {
  const counts: Partial<Record<ProposalFieldCategoryType, number>> = {};
  if (!envelope) return counts;

  for (const state of envelope.resolutionStates) {
    if (state.disposition === "Unresolved") continue;
    const category = proposalFieldForTimelinePath(state.path)?.category;
    if (!category) continue;
    counts[category] = (counts[category] ?? 0) + 1;
  }

  return counts;
}
